import { debounce, Autocomplete } from "@material-ui/core";
import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  handleRenderInput,
  handleRenderOption,
  handleGetOptionLabel,
  handleIsOptionEqualToValue
} from "./NewListingPicker";
import usePickerListingSearch from "./usePickerListingSearch";
import type { SyntheticEvent } from "react";
import type { ProcessedListingData } from "../../../@types/sharedSchema";

// Interface for our listing data
export interface ListingData extends ProcessedListingData {
  id: string;
  objectID?: string;
}

type ListingPickerProps = {
  loading?: boolean;
  selectedListing: ListingData | null;
  setSelectedListing: (listing: ListingData | null) => void;
};

export default function ListingPicker({
  loading,
  selectedListing,
  setSelectedListing
}: ListingPickerProps): JSX.Element {
  const { t: translate } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  // What is searched: the input, 300 ms after the last keystroke.
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);

  // Her own listings pinned on top, then anything else she may pick, the term read as an ADDRESS
  // and the newest term always the one shown — `usePickerListingSearch` (Trello #254). Searched
  // only while the dropdown is open, as before.
  const { listings: foundListings, loading: isLoading } = usePickerListingSearch(searchTerm, { enabled: open });

  // The currently-selected listing stays in the option list even if it is outside the current
  // result window (Autocomplete hides the value otherwise).
  const listings = useMemo((): ListingData[] => {
    const found = foundListings as ListingData[];
    if (!selectedListing || found.some((listing) => listing.id === selectedListing.id)) return found;
    return [{ ...selectedListing, objectID: selectedListing.objectID || selectedListing.id }, ...found];
  }, [foundListings, selectedListing]);

  const debouncedSetSearchTerm = useMemo(() => debounce((value: string) => setSearchTerm(value), 300), []);

  const handleInputChange = useCallback(
    (_event: SyntheticEvent, newInputValue: string) => {
      setInputValue(newInputValue);
      debouncedSetSearchTerm(newInputValue);
    },
    [debouncedSetSearchTerm]
  );

  const handleListingChange = useCallback(
    (_event: SyntheticEvent, newListing: ListingData | null) => {
      console.log("Listing selected:", newListing);
      setSelectedListing(newListing);
    },
    [setSelectedListing]
  );

  const showLoading = loading || isLoading;

  return (
    <Autocomplete
      id="listing-picker"
      fullWidth
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={listings}
      value={selectedListing}
      onChange={handleListingChange}
      onInputChange={handleInputChange}
      inputValue={inputValue}
      loading={showLoading}
      loadingText={translate("signatureSend.loadingListings")}
      noOptionsText={translate("signatureSend.noListingsFound")}
      filterOptions={(x) => x}
      renderInput={handleRenderInput(translate, showLoading)}
      renderOption={handleRenderOption(translate)}
      getOptionLabel={handleGetOptionLabel()}
      isOptionEqualToValue={handleIsOptionEqualToValue}
    />
  );
}
